import { NavLink } from 'react-router-dom'
import {
    Megaphone,
    PieChart,
    TrendingUp,
    Wallet,
    CircleAlert,
    HomeIcon,
    Search,
    Newspaper,
    Settings,
} from 'lucide-react'
import clsx from 'clsx'
import { Logo } from '@/common/assets/svg'
import useKycStore from '@/kyc/kycStore'
import { useKycCompletionState } from '@/kyc/hooks/useKycCompletionState'
import { usePreloadWallet } from '@/wallet/usePreloadWallet'
import { usePreloadPortfolio } from '@/portfolio/usePreloadPortfolio'
import { useState } from 'react'
import { useFetchTradingAccount } from '@/trading/tradingQueries'
import InstrumentSearch from './InstrumentSearch'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { NAVIGATION_COPY } from '@/locales/en'
import { APP_ROUTES } from '../constants'
import { AccountType } from '@/common/types'
import { usePwaContext } from '@/common/pwa/pwaContext'
import { Button } from '../ui/button'
import { NavigationMenuItemClickedAnalytics } from '@/common/analytics/events/NavigationMenuItemClickedAnalytics'
import { ResumeApplicationCard } from './ResumeApplicationCard'
import { useFeatureFlags } from '../featureFlags/featureFlags'

type NavItemProps = {
    icon: React.ReactNode
    title: string
    to: string
    onClick?: () => void
    onHover?: () => void
    alert?: boolean
}
const NavItem = ({ icon, title, to, onClick, onHover, alert = false }: NavItemProps) => {
    return (
        <li>
            <NavLink
                to={to}
                onClick={onClick}
                onMouseEnter={onHover}
                className={({ isActive }) => {
                    return clsx('flex space-x-3 items-center text-foreground', 'px-5 py-3 rounded-lg my-0.5', {
                        'hover:bg-lime-50': !isActive,
                        'bg-secondary-light': isActive,
                    })
                }}
            >
                {icon}
                <span className="pt-0.5">{title}</span>
                {alert && (
                    <span className="text-destructive">
                        <CircleAlert />
                    </span>
                )}
            </NavLink>
        </li>
    )
}

type Props = {
    onItemClick?: () => void
    onLogout?: () => void
}
export function AppNav({ onItemClick }: Props) {
    const { accountNavigation } = useFeatureFlags()
    const { available, openInstallModal } = usePwaContext()
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const isAccountSwitching = useAccountStore((s) => s.isAccountSwitching)
    const [hoveredWallet, setHoveredWallet] = useState(false)
    const [hoveredPortfolio, setHoveredPortfolio] = useState(false)

    const { kyc, kycComplete } = useKycStore()
    const { isInGracePeriod } = useKycCompletionState(kyc ?? undefined)
    const isAdviserActiveAccount = account?.accountType === AccountType.ADVISER
    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId, {
        enabled: Boolean(activeAccountId) && !isAdviserActiveAccount && !isAccountSwitching,
    })
    const hasApprovedTradingAccount = tradingAccount?.status === 'APPROVED'
    const shouldPreloadWallet = !isAccountSwitching && kycComplete && hoveredWallet
    const shouldPreloadPortfolio = !isAccountSwitching && kycComplete && hasApprovedTradingAccount && hoveredPortfolio

    usePreloadWallet(activeAccountId, shouldPreloadWallet)
    usePreloadPortfolio(activeAccountId, shouldPreloadPortfolio, { from: user?.createdAt })

    const handleMenuItemClick = (name: string) => {
        NavigationMenuItemClickedAnalytics.track({
            menu_name: name,
            user_type: 'customer',
        })
        if (onItemClick) {
            onItemClick()
        }
    }

    const handleAppInstall = () => {
        NavigationMenuItemClickedAnalytics.track({
            menu_name: 'install_pwa_app',
            user_type: 'customer',
        })
        openInstallModal()
    }

    return (
        <nav className="h-full flex flex-col">
            <div className="block md:hidden pl-11 pt-6 pb-8">
                <NavLink to="/">
                    <Logo width={100} height={30} />
                </NavLink>
            </div>
            <div className="mx-5 md:hidden">
                <InstrumentSearch onItemClick={onItemClick} />
            </div>
            <ul className="my-4 mx-6 h-full flex flex-col">
                {kyc && kyc.canInitiateKyc && !isInGracePeriod && !isAdviserActiveAccount && (
                    <ResumeApplicationCard
                        asListItem
                        onClick={() => onItemClick && onItemClick()}
                        to={APP_ROUTES.APPLICATION_RESUME}
                        activeOn={[APP_ROUTES.APPLICATION_RESUME, APP_ROUTES.APPLICATION_CREATE]}
                    />
                )}
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.dashboard)}
                    to={APP_ROUTES.DASHBOARD}
                    icon={<HomeIcon />}
                    title={NAVIGATION_COPY.dashboard}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.invest)}
                    to={APP_ROUTES.INVEST}
                    icon={<TrendingUp />}
                    title={NAVIGATION_COPY.invest}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.findStocks)}
                    to={APP_ROUTES.FIND_STOCKS}
                    icon={<Search />}
                    title={NAVIGATION_COPY.findStocks}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.campaigns)}
                    to={APP_ROUTES.CAMPAIGNS}
                    icon={<Megaphone />}
                    title={NAVIGATION_COPY.campaigns}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.portfolio)}
                    onHover={() => setHoveredPortfolio(true)}
                    to={APP_ROUTES.PORTFOLIO}
                    icon={<PieChart />}
                    title={NAVIGATION_COPY.portfolio}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.wallet)}
                    onHover={() => setHoveredWallet(true)}
                    to={APP_ROUTES.WALLET}
                    icon={<Wallet />}
                    title={NAVIGATION_COPY.wallet}
                />
                <NavItem
                    onClick={() => handleMenuItemClick(NAVIGATION_COPY.blogs)}
                    to={APP_ROUTES.BLOGS}
                    icon={<Newspaper />}
                    title={NAVIGATION_COPY.blogs}
                />
                {accountNavigation && (
                    <NavItem
                        onClick={() => handleMenuItemClick(NAVIGATION_COPY.adviserDesktop.account)}
                        to={APP_ROUTES.ACCOUNT}
                        icon={<Settings />}
                        title={NAVIGATION_COPY.adviserDesktop.account}
                    />
                )}

                {available && (
                    <li className="mt-auto">
                        <Button variant="tertiary" onClick={handleAppInstall}>
                            {NAVIGATION_COPY.installPwaApp}
                        </Button>
                    </li>
                )}
            </ul>
        </nav>
    )
}
